import axios from 'axios';
import * as log from 'loglevel';
import envConfig from '../utils/getEnv';
import { performAutoLogout } from '../utils/sessionTimeout';

const timeout = 600000;
const { baseUrl } = envConfig;
const maxContentLength = 31457280;

const instance = axios.create({
  timeout,
  baseUrl,
  maxContentLength,
  headers: { 'Accept-Language': 'en' },
});

// Shared state used by both request and response interceptors. Lifting these
// to module scope means a token refresh triggered from the response side is
// immediately visible to subsequent outgoing requests.
let cachedToken = null;
let loginHandler = null;
let isValidTokenHandler = () => true;

// Single in-flight refresh promise so that several requests failing with 401
// at the same time only trigger one underlying token-renewal call.
let refreshPromise = null;

const refreshAccessToken = () => {
  if (!loginHandler) {
    return Promise.reject(new Error('No token refresh handler configured'));
  }
  if (!refreshPromise) {
    refreshPromise = Promise.resolve()
      .then(() => loginHandler())
      .then(newToken => {
        if (newToken) {
          cachedToken = newToken;
        }
        return newToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

export const getCachedAccessToken = () => cachedToken;
export { refreshAccessToken };

/**
 * Detects MSAL / B2C errors that mean the user MUST re-authenticate
 * interactively — i.e. silent token acquisition cannot recover. Examples:
 *  - AADB2C90077: "No existing session and prompt=none"
 *  - AADB2C90080: SSO session expired
 *  - errorCode interaction_required / login_required / consent_required
 * In any of these cases there is no silent path forward and we must drive
 * the user through a full sign-in flow (handled via performAutoLogout).
 */
export const isInteractionRequiredError = err => {
  if (!err) return false;
  const code = String(err.errorCode || err.code || '').toLowerCase();
  const text = String(err.errorMessage || err.message || err).toLowerCase();
  return (
    code === 'interaction_required' ||
    code === 'login_required' ||
    code === 'consent_required' ||
    text.includes('aadb2c90077') ||
    text.includes('aadb2c90080') ||
    text.includes('interaction_required') ||
    text.includes('login_required') ||
    text.includes('consent_required')
  );
};

let authExpiryInterceptorAttached = false;

/**
 * Registers a response interceptor that, on a 401 (expired/invalid session),
 * silently refreshes the MSAL access token and retries the original request
 * once. Auto-logout on refresh failure is intentionally disabled (commented
 * out below) — the failing request will simply reject and the user stays
 * signed in. Re-enable the `performAutoLogout('expired')` line if you want
 * the previous "logout on hard-failure" behaviour back.
 *
 * Safe to call multiple times — only the first call attaches.
 */
export const attachAuthExpiryInterceptor = () => {
  if (authExpiryInterceptorAttached) return;
  authExpiryInterceptorAttached = true;

  instance.interceptors.response.use(
    response => response,
    async error => {
      const status = error && error.response && error.response.status;
      const originalRequest = error && error.config;

      if (
        status !== 401 ||
        !originalRequest ||
        // eslint-disable-next-line no-underscore-dangle
        originalRequest._retryWithRefresh
      ) {
        return Promise.reject(error);
      }

      // eslint-disable-next-line no-underscore-dangle
      originalRequest._retryWithRefresh = true;

      let refreshError = null;
      try {
        const newToken = await refreshAccessToken();
        if (newToken) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return instance(originalRequest);
        }
        log.warn('Token refresh returned no token');
      } catch (e) {
        refreshError = e;
        log.error('Token refresh failed after 401', e);
      }

      // Refresh failed (threw, or resolved with no token). There is no silent
      // recovery path — drive the user through a fresh sign-in.
      // performAutoLogout is idempotent (guarded by a `loggingOut` flag), so
      // multiple concurrent 401s racing here will only trigger one logout.
      const reason = isInteractionRequiredError(refreshError)
        ? 'interaction_required'
        : 'expired';
      performAutoLogout(reason);
      return Promise.reject(error);
    },
  );
};

/**
 * Change the language in request header
 * @param {string} languageCode - Language code (en, es, zh, fr, de, ar, nl, tr, it, ja, th, …)
 */
export const updateAxiosInstanceLanguage = languageCode => {
  instance.defaults.headers['Accept-Language'] = languageCode;
};

export const updateAxiosInstanceResponseType = () => {
  instance.defaults.responseType = 'arraybuffer';
};

export const resetAxiosInstanceResponseType = () => {
  instance.defaults.responseType = '';
};

export const updateAxiosTimeout = axiosTimeout => {
  instance.defaults.timeout = axiosTimeout;
};

export const updateAxiosInstanceWithErrorCode = (
  translationFailed,
  unsupportedLanguage,
) => {
  instance.interceptors.response.use(
    response =>
      // if (response.status === 206) {
      //   translationFailed(true);
      // }
      response,
    error => {
      if (error.response.status === 406) {
        unsupportedLanguage(true);
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject('');
      }
      return Promise.reject(error);
    },
  );
};

let requestAuthInterceptorAttached = false;

const attachRequestAuthInterceptor = () => {
  if (requestAuthInterceptorAttached) return;
  requestAuthInterceptorAttached = true;

  instance.interceptors.request.use(
    config => {
      /* eslint-disable no-param-reassign */
      const tokenValid = isValidTokenHandler();
      if (tokenValid && cachedToken) {
        config.headers.Authorization = `Bearer ${cachedToken}`;
        return config;
      }
      log.info('Token expired, trying to renew');
      // If MSAL silent renewal succeeds, the request proceeds with the new
      // token. If it falls back to a redirect, the page navigates away and
      // the in-flight request is effectively aborted.
      return refreshAccessToken()
        .then(newToken => {
          if (newToken) {
            config.headers.Authorization = `Bearer ${newToken}`;
          }
          return config;
        })
        .catch(e => {
          log.error(e);
          return config;
        });
      /* eslint-enable no-param-reassign */
    },
    error => Promise.reject(error),
  );
};

/**
 * Updates the axios instance token and refresh wiring.
 * @param {string} token - access-token
 * @param {function} login - The login/refresh function (e.g. msal getToken)
 * @param {function} isValidToken - Returns true if the current token is not expired
 */
export const updateAxiosInstance = (token, login, isValidToken) => {
  cachedToken = token;
  loginHandler = login;
  if (typeof isValidToken === 'function') {
    isValidTokenHandler = isValidToken;
  }
  attachRequestAuthInterceptor();
};

export default instance;
